/* eslint-disable no-continue */
/* eslint-disable no-param-reassign */
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { Change } from "diff";
import { isEqual, uniqueId } from "lodash";
import isBetweenInclusive from "../../util/isBetweenInclusive";
import { Path, ROOT_PATH } from "../../writeshift/Path";
import { AdviceLevel, Feedback } from "../../writeshift/GrammarScore";
import {
  TreeRange,
  getLocationOrder,
  TreeRangeDelta,
  locationIntersectsRange,
  pathIntersectsRange,
  pathAfterChange,
} from "../../writeshift/Range";
import { FeedbackWithRange } from "../../writeshift/WriteShift";
import { RectLayout } from "../../GeometricLayout";
import { isValue } from "../../useOnTextEdit";
import FeedbackType from "../../FeedbackType";

/**
 * Feedback from the network with the corresponding UI information.
 */
export interface WritingAlert {
  id: number;

  /**
   * The editor to which this alert belongs
   */
  editorId: number;

  /**
   * Original feedback from the network, could contain range information
   * that is out of sync with the UI.
   */
  feedback: FeedbackWithRange;

  /**
   * The score (a group of feedback) to which this feedback belongs.
   *
   * See https://docs.writeshift.com/#making-a-request for more info.
   */
  scoreId: string;

  /**
   * The plain text phrase underlined by this alert.
   *
   * This does not include any markup that might be present within the actual ui.
   */
  phrase: string;

  /**
   * Current UI state, might differ from the original
   * feedback from the network
   */
  ui: {
    /**
     * This range might be different from the one in feedback,
     * because the user could edit the middle of a piece of writing.
     * The ui updates right away, without waiting for a network response to redraw alerts,
     * so the ranges of underlines after the edit are shifted down right away,
     * (so they stay underneath the original word or phrase)
     */
    range: TreeRange;

    /**
     * The positions of the alert's underlines, relative to the overlay.
     *
     * This is an array of positions because it could be a phrase that spans
     * many lines.
     */
    overlayPositions: RectLayout[];

    /**
     * If the range changes, indicates the overlayPosition and boundingClientRect
     * need to be updated.
     */
    staleRect: boolean;
  };
}

export function getFeedbackType(a: WritingAlert): FeedbackType {
  // Type Safety: rule class category is only "SPELLING" or "GRAMMAR", which
  // when transformed by toLowerCase are valid types of the FeedbackType;
  return a.feedback.ruleClassCategory.toLowerCase() as FeedbackType;
}

export function getLowestLevelAdviceText(a: WritingAlert): string {
  const advice = a.feedback.adviceLevels;

  const LEVEL_ORDERING: AdviceLevel[] = [1, 2, 3];

  for (const i of LEVEL_ORDERING) {
    const adviceText = advice[i];
    if (adviceText) {
      return adviceText;
    }
  }
  return "";
}

function sortByOffsetAscendingInEditors(
  a: WritingAlert,
  b: WritingAlert
): number {
  if (a.editorId !== b.editorId) {
    return a.editorId - b.editorId;
  }
  const aIsBefore =
    getLocationOrder(a.ui.range.start, b.ui.range.start) === "BEFORE";
  if (aIsBefore) {
    return -1;
  }
  // b is before a, or they're equal. We don't care about the equal case
  return 1;
}

function getPriority(feedback: Feedback): number {
  return feedback.ruleClassCategory === "SPELLING" ? 1 : 0;
}

export const alertsAdapter = createEntityAdapter<WritingAlert>({
  selectId: (writingAlert) => writingAlert.id,
  sortComparer: sortByOffsetAscendingInEditors,
});

export interface FeedbackFromEditor {
  editorId: number;
  scoreId: string;
  feedbackWithPhrase: {
    feedback: FeedbackWithRange;
    phrase: string;
  }[];
}

export interface TextChange {
  textEdits: Change[];
  nodePath: Path;
}
export interface NodeRemoval {
  removedPath: Path;
}
export interface NodeAddition {
  addedPath: Path;
}

export type RichEdit = TextChange | NodeRemoval | NodeAddition;

export const writingAlertsSlice = createSlice({
  name: "WritingAlerts",
  initialState: alertsAdapter.getInitialState(),
  reducers: {
    // FIXME(performance): Currently O(M * N) where M is the number of new piece of feedback,
    // and N is the number of old pieces of feedback. This definitely could be optimized because
    // we're not taking advantage of the fact that feedback from the API comes sorted by offset (I think?)
    // and the old feedback is definitely sorted by offset.
    // Estimated optimal performance: O(M + N)
    // Solution: if both are sorted, a two pointer algorithm that increments pointers based on offset position
    // and upserts when necessary, similar to the merge() function in mergesort.
    refreshFeedback: (state, action: PayloadAction<FeedbackFromEditor>) => {
      const { editorId, feedbackWithPhrase, scoreId } = action.payload;

      const oldFeedback = state.ids
        .filter((id) => state.entities[id]?.editorId === editorId)
        .map((i) => state.entities[i]);

      alertsAdapter.removeMany(
        state,
        state.ids.filter((id) => state.entities[id]?.editorId === editorId)
      );

      // Ignore the next piece of feedback because it's lower priority and overlapping with a previous one
      let ignoreNext = false;

      // If there's a newPiece of feedback that matches the stale UI position,
      // then we search for alerts at that offset to
      for (const [i, { feedback: newPiece, phrase }] of Object.entries(
        feedbackWithPhrase
      )) {
        const index = Number.parseInt(i, 10);

        if (ignoreNext) {
          ignoreNext = false;
          continue;
        }

        // Check exactly overlapping feedback, only store high priority one
        if (index !== feedbackWithPhrase.length - 1) {
          const next = feedbackWithPhrase[index + 1];
          if (isEqual(newPiece.highlightRange, next.feedback.highlightRange)) {
            // get the priority of both
            const p1 = getPriority(newPiece);
            const p2 = getPriority(next.feedback);
            if (p1 < p2) {
              continue;
            } else {
              ignoreNext = true;
            }
          }
        }

        // TODO(perf): This `find` call is bad for performance, because it's O(N)
        // If the highlight offset and rule class id match, then it's reasonable to assume it's the same one.
        const oldPiece = oldFeedback.find(
          (o) =>
            isEqual(o?.ui.range, newPiece.highlightRange) &&
            o?.feedback.ruleClassId === newPiece.ruleClassId &&
            // these might be different if the user switched the langauge
            isEqual(o?.feedback.adviceLevels, newPiece.adviceLevels)
        );

        if (oldPiece) {
          // Refresh instead of replace to preserve identity
          oldPiece.ui.staleRect = false;
          oldPiece.scoreId = scoreId;
          oldPiece.feedback.highlightRange = newPiece.highlightRange;
          alertsAdapter.addOne(state, oldPiece);
        } else {
          // add the new piece to the new state
          const newAlert = {
            id: Number.parseInt(uniqueId(), 10), // FIXME: this id generation should happen in action creator, not the reducer!
            editorId,
            feedback: newPiece,
            scoreId,
            phrase,
            ui: {
              range: newPiece.highlightRange,
              overlayPositions: [],
              staleRect: true,
            },
          };
          alertsAdapter.addOne(state, newAlert);
        }
      }
    },
    // TODO(perf): these styleEdits should be guaranteed to be in some order to reduce time complexity
    styleEdits: (
      state,
      action: PayloadAction<{ styleEdits: TreeRangeDelta[]; editorId: number }>
    ) => {
      const { editorId, styleEdits } = action.payload;
      for (const edit of styleEdits) {
        if (edit.delta === null) {
          // removal
          for (const id of state.ids) {
            // Type Safety: redux kit guarantees ids and entities are in sync
            const alert = state.entities[id]!;
            if (alert.editorId !== editorId) {
              continue;
            }
            const alertRange = alert.ui.range;

            const alertIsWithinDelta =
              locationIntersectsRange(alertRange.start, edit.range) ||
              locationIntersectsRange(alertRange.end, edit.range);

            if (!alertIsWithinDelta) {
              continue;
            }
            // Post condition: alert is within the delta
            // Remove it
            alertsAdapter.removeOne(state, id);
          }
        } else {
          // We're shifting the node over, as well as cutting off some of its prefix
          const { delta, range } = edit;

          const editLevel = range.start.index.length - 1;
          let cumulativeIndexShift = 0;

          // TODO(refactor): NOT DRY, doing this a lot
          for (const id of state.ids) {
            const alert = state.entities[id]!;
            if (alert.editorId !== editorId) {
              continue;
            }
            const alertRange = alert.ui.range;
            const alertIsWithinDelta =
              pathIntersectsRange(alertRange.start.index, range) &&
              pathIntersectsRange(alertRange.end.index, range);
            if (!alertIsWithinDelta) {
              // if the start of the alert is after the end of the edit
              if (
                getLocationOrder(alert.ui.range.start, range.end) === "AFTER"
              ) {
                // shift the level
                alert.ui.range.start.index[editLevel] += cumulativeIndexShift;
                alert.ui.range.end.index[editLevel] += cumulativeIndexShift;
              }
            }
            // These are shifts to be registered
            const { offset, lastIndex } = delta;

            // Shift offsets
            alertRange.start.offset += offset;
            alertRange.end.offset += offset;

            // Shift index
            alertRange.start.index[alertRange.start.index.length - 1] +=
              lastIndex;
            alertRange.end.index[alertRange.end.index.length - 1] += lastIndex;

            // Shift subsequent alerts
            cumulativeIndexShift += lastIndex;
          }
        }
      }
      return state;
    },
    richEdits: (
      state,
      action: PayloadAction<{ richEdits: RichEdit[]; editorId: number }>
    ) => {
      const { payload } = action;
      const { editorId, richEdits } = payload;
      richEdits.forEach((richEdit) => {
        // FIXME(performance): alerts are guaranteed to be sorted left to right, top to bottom in the tree.
        // Currently O(M*N) where M is number of edits, N is number of alerts
        // Alert ids are sorted by offset, and edits are sorted by offset,
        // So theoretically we could get down to O(M+N) time complexity.
        // In order to achieve this, we might need to binary search to find the start
        // of the underlines for the given editor which would be O(log(E)) where E is the
        // number of attached editors. So it might be more like O(M+N+log(E))

        // #region text change
        if ("textEdits" in richEdit) {
          // text change
          let currentEditOffset = 0;
          // find the alerts that intersect this change
          for (const textEdit of richEdit.textEdits) {
            const editLength = textEdit.count || 0;
            if (isValue(textEdit)) {
              currentEditOffset += editLength;
              continue;
            }
            for (const id of state.ids) {
              // Type safety: state.ids is guaranteed to be in sync with entities
              const alert = state.entities[id] as WritingAlert;
              if (!alert || alert.editorId !== editorId) continue;

              // Ensure all the alerts after this edit are marked as stale
              const order = getLocationOrder(alert.ui.range.start, {
                index: richEdit.nodePath,
                offset: 0,
              });
              const shouldMarkStale = order === "AFTER" || order === "AT";
              if (shouldMarkStale) {
                alert.ui.staleRect = true;
              }

              // If the path doesn't intersect, we don't care about it
              if (!pathIntersectsRange(richEdit.nodePath, alert.ui.range)) {
                continue;
              }

              // We should remove the alert if the edit touches alert
              const location = {
                index: richEdit.nodePath,
                offset: currentEditOffset,
              };

              // if the location of the edit intersects the range...
              if (locationIntersectsRange(location, alert.ui.range)) {
                alertsAdapter.removeOne(state, alert.id);
                continue;
              }

              const start = alert.ui.range.start.offset;
              const end = alert.ui.range.end.offset;

              const editStart = currentEditOffset;
              const editEnd = currentEditOffset + editLength;

              const alertBoundaryIsWithinEdit =
                isBetweenInclusive(start, editStart, editEnd) ||
                isBetweenInclusive(end, editStart, editEnd);

              const editBoundaryIsWithinAlert =
                isBetweenInclusive(editStart, start, end) ||
                isBetweenInclusive(editEnd, start, end);

              // If alert or edit come into contact, we remove the underline.
              if (alertBoundaryIsWithinEdit || editBoundaryIsWithinAlert) {
                alertsAdapter.removeOne(state, id);
              } else if (editStart < start) {
                let shift = 0;
                if (textEdit.added) {
                  shift = editLength;
                } else if (textEdit.removed) {
                  shift = -editLength;
                }

                // shift()
                alert.ui.range.start.offset += shift;
                alert.ui.range.end.offset += shift;
                alert.ui.staleRect = true;
              }
            }
            currentEditOffset += editLength;
          }
          // #endregion
        } else {
          // we have to update the paths of all subsequent alerts
          for (const id of state.ids) {
            // Type Safety: redux guarantees id will point to a defined entity
            const alert = state.entities[id]!;
            if (alert.editorId !== editorId) continue;

            let path: Path;
            let type: "REMOVED" | "ADDED";
            if ("removedPath" in richEdit) {
              path = richEdit.removedPath;
              type = "REMOVED";
            } else {
              path = richEdit.addedPath;
              type = "ADDED";
            }

            const { start, end } = alert.ui.range;

            // Shift all subsequent alert paths
            const newStartPath = pathAfterChange(start.index, {
              path,
              type,
            });
            const newEndPath = pathAfterChange(end.index, {
              path,
              type,
            });

            // an edit touched the alert at that path
            const editIntersectsAlertRange = locationIntersectsRange(
              { index: path, offset: 0 },
              alert.ui.range
            );
            if (
              newStartPath === null ||
              newEndPath === null ||
              editIntersectsAlertRange
            ) {
              alertsAdapter.removeOne(state, alert.id);
            } else {
              // Update the alerts
              alert.ui.range.start.index = newStartPath;
              alert.ui.range.end.index = newEndPath;
            }
          }
        }
      });
    },

    recalculateRects: (
      state,
      action: PayloadAction<{ id: number; overlayPositions: RectLayout[] }[]>
    ) => {
      for (const idRect of action.payload) {
        for (const id of state.ids) {
          // Type safety: redux guarantees that the entities will be present in the entities array
          const alert = state.entities[id]!;
          if (alert.id === idRect.id) {
            alert.ui.overlayPositions = idRect.overlayPositions;
            alert.ui.staleRect = false;
          }
        }
      }
    },
    applyReplacement: alertsAdapter.removeOne,

    /**
     * Removes all the alerts for the given editor because we don't know where to put them.
     *
     * Called when the rich edits are just too rich.
     *
     * When all else fails punt.
     */
    punt(
      state,
      action: PayloadAction<{ editorId: number; records?: MutationRecord[] }>
    ) {
      for (const id of state.ids) {
        const alert = state.entities[id];
        if (alert?.editorId === action.payload.editorId) {
          alertsAdapter.removeOne(state, alert.id);
        }
      }
    },
  },
});

/**
 * A convenience action creator made for textareas, where the default path for
 * text edits is within the root.
 *
 * @param editorId
 * @param textChange changes generated from the root, which should be a text node
 * @returns
 */
export function plainTextEdits(
  editorId: number,
  edits: Change[]
): PayloadAction<{
  richEdits: RichEdit[];
  editorId: number;
}> {
  const richEdits = [
    {
      textEdits: edits,
      nodePath: ROOT_PATH,
    },
  ];

  return writingAlertsSlice.actions.richEdits({
    richEdits,
    editorId,
  });
}

export const {
  recalculateRects,
  refreshFeedback,
  applyReplacement,
  richEdits,
  styleEdits,
  punt,
} = writingAlertsSlice.actions;
export default writingAlertsSlice.reducer;
